import styled from 'styled-components';
import { fleetLeading, fleetType } from '@entities/fleet';
import { DataTable, NARROW, cellLink } from '../ui';

export const PanelRoot = styled.div`
  position: relative;
`;

export const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const Count = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 32px;
  padding: ${({ theme }) => `0 ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.status.danger}66;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.danger};
  }

  &:hover,
  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.status.danger}14;
  }

  &[aria-pressed='true'] {
    font-weight: 600;
  }
`;

export const Table = styled(DataTable)`
  margin-top: ${({ theme }) => theme.spacing(4)};

  th.c-name {
    min-width: 180px;
    font-weight: 400;
  }

  th.c-name > .name {
    display: block;
    font-size: ${fleetType.base};
    font-weight: 500;
    line-height: ${fleetLeading.tight};
    overflow-wrap: anywhere;
  }

  th.c-name .comment {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Фокус сюда ставит программа (кнопка строки исчезла после записи) — рамка
     показывает, где продолжить. */
  th.c-name:focus-visible {
    border-radius: ${({ theme }) => theme.radius.xs};
  }

  td.c-projects {
    min-width: 160px;
    max-width: 300px;
    overflow-wrap: anywhere;
  }

  td.c-projects a {
    ${cellLink}
  }

  td.c-projects .project + .project {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  td.c-projects .project {
    display: block;
  }

  td.c-projects .how {
    margin-left: ${({ theme }) => theme.spacing(1.5)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
  }

  td.c-center {
    min-width: 200px;
  }

  td.c-center .code {
    font-weight: 600;
  }

  td.c-state {
    min-width: 200px;
    max-width: 360px;
  }

  /* Кромка строки, которой нужно внимание, — фоном, а не тенью: тень занята
     кольцом фокуса (на название строки фокус ставится после записи). */
  tr.row[data-attention='true'] > th:first-child {
    background-image: linear-gradient(
      ${({ theme }) => theme.colors.status.warning},
      ${({ theme }) => theme.colors.status.warning}
    );
    background-size: 3px 100%;
    background-repeat: no-repeat;
  }

  tr.row[data-state='conflict'] > th:first-child {
    background-image: linear-gradient(
      ${({ theme }) => theme.colors.status.danger},
      ${({ theme }) => theme.colors.status.danger}
    );
  }

  tr.editor > td {
    padding-top: 0;
    background-color: ${({ theme }) => theme.colors.surface};
  }

  tr.row[data-editing='true'] > * {
    background-color: ${({ theme }) => theme.colors.surface};
    border-bottom-color: transparent;
  }

  @container chronum (max-width: ${NARROW}) {
    &,
    & > tbody {
      display: block;
    }

    & > thead {
      display: none;
    }

    & > tbody > tr.row {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      row-gap: ${({ theme }) => theme.spacing(1.5)};
      padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)}`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > tbody > tr.row > th,
    & > tbody > tr.row > td {
      display: block;
      min-width: 0;
      max-width: none;
      padding: 0;
      border: none;
      background: none;
    }

    /* На телефоне кромку несёт вся карточка, а не ячейка названия. */
    tr.row[data-attention='true'] {
      background-image: linear-gradient(
        ${({ theme }) => theme.colors.status.warning},
        ${({ theme }) => theme.colors.status.warning}
      );
      background-size: 3px 100%;
      background-repeat: no-repeat;
    }

    tr.row[data-state='conflict'] {
      background-image: linear-gradient(
        ${({ theme }) => theme.colors.status.danger},
        ${({ theme }) => theme.colors.status.danger}
      );
    }

    tr.row[data-attention='true'] > th:first-child,
    tr.row[data-state='conflict'] > th:first-child {
      background-image: none;
    }

    /* Пустая стройка на телефоне — лишняя строка: состояние и так видно. */
    & > tbody > tr.row > td.c-projects.empty {
      display: none;
    }

    & > tbody > tr.row[data-editing='true'] {
      background-color: ${({ theme }) => theme.colors.surface};
    }

    & > tbody > tr.editor {
      display: block;
    }

    & > tbody > tr.editor > td {
      display: block;
      padding: ${({ theme }) => `0 0 ${theme.spacing(3)}`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    .narrow-only {
      display: block;
    }
  }
`;

export const NarrowLabel = styled.span`
  display: block;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CenterLine = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
`;

export const RowError = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const historyTag = (siteId: string) =>
  ({ type: 'EconChronum', id: `HISTORY:site:${siteId}` }) as const;
